/* eslint-disable eqeqeq, no-await-in-loop, no-continue */
import {
  applySectionSizer,
  applyTunnelSizer,
  applyTunnelSizerBulk,
  applyUnitSizer,
  loadAhusByProjectId,
  loadEditableProjects,
  loadPinnedProjects,
  loadSectionsByTunnelId,
  loadTunnelIndexes,
  loadTunnelsByAhuId,
  type ISizerPopupProject,
  type ISizerPopupSection,
  type ISizerPopupTunnel,
  type ISizerPopupTunnelIndex,
} from 'app/services/sizerPopupApi';
import {
  readSizerAirflow,
  readSizerPopupSelection,
  readSizerSettings,
  type SizerPopupSelection,
} from '../sizerStorage';
import {
  POPUP_STATES,
  readDoesntNeedUpdate,
  readDraggablePosition,
  readFieldsStatus,
  readPopupState,
  removeDoesntNeedUpdate,
  removePopupState,
  setDoesntNeedUpdate,
  writeDraggablePosition,
  writeFieldsStatus,
  writePopupState,
  clearDraggablePosition,
  SIZER_POPUP_FIELDS_STATUS_KEY,
  SIZER_POPUP_STATE_KEY,
  type DraggablePopupPosition,
  type SizerPopupFieldsStatus,
  type SizerPopupState,
} from './sizerPopupStorage';
import { SIZER_AIRFLOW_KEY, SIZER_POPUP_KEY, removeLocalStorageItem } from '../sizerStorage';

/**
 * Phase 4e — `django/static/mvc/header/view/sizer_popup.js` (1217 LOC), the AHU
 * SELECTION POPUP: the popup a matrix-cell click opens, and the ONLY server
 * write path in the whole `/sizer/` feature (discovery §4b).
 *
 * ★ WHY A CONTROLLER CLASS AND NOT HOOKS — the same call the matrix made
 * (`matrixEngine.ts`). The Backbone original is a state machine whose steps read
 * and write ONE mutable bag (`self.fieldsStatus`) inside a single async turn,
 * re-entering itself through `updateProjects → updateAHUs → updateTunnels →
 * updateSections`, each mutating the SAME `overrides` object it was handed, and
 * discarding its own results against three monotonically-increasing version
 * counters. That is a mutable object graph; modelling it as React state would
 * mean either batching away the intermediate mutations the next step depends on,
 * or a fan of refs that IS this class with extra steps. The class is the honest
 * model; `SizerSelectionPopup.tsx` subscribes and renders from it.
 *
 * ★ THE HEADER-OWNED BEHAVIOURS — all of these live in the legacy HEADER, not in
 * `sizer_popup.js`. 4e ported the two that were observable on a single `/sizer/`
 * tab and recorded the rest as scope; the popup now lives on every board
 * (`sizerSelectionHost.ts`), which made the rest observable too:
 *
 *  - `header.js:227-231` — RESTORE-ON-LOAD (`restoreFromStorage` below). Without
 *    it a minimized popup vanished on reload.
 *  - `header.js:431-453` — `forceUpdateAHUs`/`forceUpdateTunnels`, but only for
 *    the write THIS page performs (`scheduleForceUpdateAfterApply` below).
 *    Without it CURRENT OD W x H kept showing the pre-APPLY dimensions.
 *  - `header.js:213-224` + `sizer_popup.js:140-142` — the cross-TAB `storage`
 *    listeners for `sizer_popup`, `sizer_popup_state` and
 *    `sizer_popup_fields_status` (`attachStorageListeners` below). These are how
 *    a `/sizer/` tab opens the same window on the AHU board / dashboard /
 *    project board tab.
 *
 *  STILL NOT PORTED:
 *  - the syncdata socket fan-out (`header.js:247-355`) as it applies to the
 *    POPUP's own lists, i.e. a refresh driven by ANOTHER user's edit.
 *    `sizer_doesnt_need_update` is written and read, so tabs agree on the
 *    mute. The AHU BOARD's half of that fan-out — the tunnel
 *    W/H + canvas refresh after a sizer APPLY — is ported in
 *    `pages/ahu-board/hooks/useAhuBoardSyncdata.ts`.
 */

/** `INDEX_FIELD_MAPPING` (sizer_popup.js:59-72). Keys 1-3 (the fan rows) are
 * COMMENTED OUT upstream; `__render_popup:564-571` would fill 0-2 from the
 * label's `for` attribute, but the `#Fan` form renders no `.section_select` at
 * all (sizer_popup.tmpl:92-133), so that branch is dead and indices 0-2 stay
 * undefined. */
export const INDEX_FIELD_MAPPING: Record<number, 'cooling_coil' | 'heating_coil' | 'filter'> = {
  3: 'cooling_coil',
  4: 'cooling_coil',
  5: 'cooling_coil',
  6: 'heating_coil',
  7: 'heating_coil',
  8: 'heating_coil',
  9: 'filter',
  10: 'filter',
  11: 'filter',
};

/** `FIELD_COMPONENT_MAPPING` (sizer_popup.js:74-81) — the `fan` entries are live
 * in the object but unreachable, see above. */
export const FIELD_COMPONENT_MAPPING: Record<string, 'fan' | 'coil' | 'filter'> = {
  plenum: 'fan',
  cube: 'fan',
  plug: 'fan',
  cooling_coil: 'coil',
  heating_coil: 'coil',
  filter: 'filter',
};

/** The nine `.section_select` ids the template renders (sizer_popup.tmpl:158,
 * 184, 210, 237, 263, 289, 315, 339, 363). */
export const SECTION_INDICES = [3, 4, 5, 6, 7, 8, 9, 10, 11] as const;

/**
 * The subset `close_popup` / `changeProject` / `changeAHU` blank
 * (sizer_popup.js:777-782, :1024-1029, :1056-1061).
 * ⚠ 5, 8 and 11 are MISSING from all three lists — a stale third-row selection
 * survives a project or AHU change. Reproduced verbatim; `updateSections` is
 * what actually clears it, and only if the id stops existing.
 */
const RESET_SECTION_KEYS = [
  'section_3_select_value',
  'section_4_select_value',
  'section_6_select_value',
  'section_7_select_value',
  'section_9_select_value',
  'section_10_select_value',
] as const;

/** `fn.uni_tools.deepCompare` (universal_fn_tools.js:676+) — structural, so
 * key ORDER does not count. Used only to skip a no-op localStorage write. */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') return Number.isNaN(a) && Number.isNaN(b);
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a as object);
  const kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  return ka.every((k) =>
    deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])
  );
}

export interface SectionOption {
  value: string;
  label: string;
}

export class SizerSelectionController {
  /** `self.current_state` (sizer_popup.js:99). */
  state: SizerPopupState = POPUP_STATES.INITIAL;

  /** `self.data` — the clicked cell's `selectionData` (main.js:615-624). */
  data: SizerPopupSelection | null = null;

  /** `self.fieldsStatus` — the one mutable bag. */
  fieldsStatus: SizerPopupFieldsStatus = {};

  /** `self.confirmation_state` (:104) — drives `.non-clickable` on APPLY. */
  confirmationState = false;

  /** `self.tunnel_indexes_mapping` (:103, filled at :444-446). */
  tunnelIndexesMapping: Record<number, ISizerPopupTunnelIndex> = {};

  /**
   * ★ NOT a legacy field — the BACKDROP-dismiss flag.
   *
   * On `/sizer/` two independent handlers see a click on the popup's dimmed
   * area: the page's blanket body handler strips `.active` from every
   * `.popup-wrapper.active` (`sizer/mvc/view/main.js:161-167`) while the popup's
   * OWN state machine is untouched, so `current_state` stays `opened` and
   * `sizer_popup_state` stays `"opened"`. The popup is invisible but alive:
   * clicking another matrix cell calls `update()`, which re-renders the template
   * whose root class hardcodes `active` (sizer_popup.tmpl:1) and brings it back
   * with every selection intact. Esc is DIFFERENT — the popup binds its own
   * `$(document).keyup` (:143-146) and genuinely `close_popup()`s.
   *
   * (The sizer view also binds `click .sizerSelection → eventStop`
   * (main.js:56), which would have stopped this — but its `el` is
   * `#ksdm-content` and `.sizerSelection` lives in the header's `#sizer_popup`,
   * so the delegated handler can never match. Dead; the dismissal is real.)
   */
  backdropDismissed = false;

  /** `self.saved_projects` / `self.saved_pinned_projects` (:131, :164-165). */
  private savedProjects: ISizerPopupProject[] | undefined;
  private savedPinnedProjects: ISizerPopupProject[] | undefined;

  /** SearchableSelect paging state (:437, :1016, :1198-1213). */
  page = 1;
  private pagination: ISizerPopupProject[] = [];
  private filtered = '';
  private loadingMore = false;

  /**
   * The list currently shown in the project dropdown. `__render_projects` resets
   * it to `[...pinned_projects, ...projects]` (:593-598, via
   * `SearchableSelect.update`); `searchProjects`/`scrollProjects` replace it
   * without touching `fieldsStatus` (`SearchableSelect.updateList`, :157, :1209).
   */
  projectList: ISizerPopupProject[] = [];

  /** `self.__ahusVersion` / `__tunnelsVersion` / `__sectionsVersion` (:118-120). */
  private ahusVersion = 0;
  private tunnelsVersion = 0;
  private sectionsVersion = 0;

  /** `self.__loadingProjects` — the `get_waiter()` promise (:114-115, :443). */
  private loadingProjects: Promise<void>;

  private destroyed = false;
  private version = 0;
  private listeners = new Set<() => void>();

  // -------------------------------------------------------------------------
  // subscription plumbing (React's replacement for `__render_*`)
  // -------------------------------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getVersion = (): number => this.version;

  private emit(): void {
    if (this.destroyed) return;
    this.version += 1;
    this.listeners.forEach((l) => l());
  }

  // -------------------------------------------------------------------------
  // initialize — sizer_popup.js:96-149
  // -------------------------------------------------------------------------

  constructor() {
    let finish: () => void = () => {};
    this.loadingProjects = new Promise<void>((resolve) => {
      finish = resolve;
    });
    void this.initialize(finish);
  }

  private async initialize(finish: () => void): Promise<void> {
    this.pullPopupFieldsStatus(false); // :105
    this.fieldsStatus.projects = []; // :106-109
    this.fieldsStatus.ahus = [];
    this.fieldsStatus.tunnels = [];
    this.fieldsStatus.sections = {};
    await this.pushWholeFieldsStatus(); // :110
    if (this.destroyed) return;

    const overrides: SizerPopupFieldsStatus = {}; // :112
    await this.updateProjects(overrides, true); // :129-139
    if (this.destroyed) return;
    this.overrideAndPushFieldsStatus(overrides); // :130
    if (overrides.projects) this.savedProjects = overrides.projects; // :131
    finish(); // :132
    this.attachStorageListeners(); // header.js:216-224 + sizer_popup.js:140-142
    void this.restoreFromStorage();
  }

  /**
   * THE CROSS-TAB CHANNEL — `header.js:216-224` plus the popup's own
   * `sizer_popup.js:140-142`, the three `fn.local_storage.add_listener` calls
   * 4e recorded as "not ported, no observable effect on a single `/sizer/` tab".
   * With the popup now living on every board they are the whole point: a cell
   * click or an APPLY in the `/sizer/` tab writes `sizer_popup`,
   * `sizer_popup_state` and `sizer_popup_fields_status`, and THESE are what make
   * the AHU-board / dashboard / project-board tab open the same window with the
   * same selections. That is the behaviour QA compared against dev.
   *
   * `fn.local_storage.add_listener` is a bare `window.addEventListener('storage')`
   * filtered on `e.key == key || !e.key` (`js_scripts/local_storage.js:32-38`) —
   * the null key being a `localStorage.clear()`, which is delivered to every
   * listener. Native `storage` events do NOT fire in the writing tab, so this is
   * cross-tab only in both stacks and cannot loop back on itself.
   */
  private attachStorageListeners(): void {
    window.addEventListener('storage', this.onStorage);
  }

  private onStorage = (e: StorageEvent): void => {
    if (this.destroyed) return;
    // header.js:216-218 → `processSizerData` → `update(data)` (:487-490).
    if (e.key === SIZER_POPUP_KEY || !e.key) {
      const data = readSizerPopupSelection();
      if (data) this.update(data);
    }
    // sizer_popup.js:140-142 — the popup's own bag listener. `render` is true,
    // i.e. re-render when OPENED; `pullPopupFieldsStatus` emits unconditionally
    // here because React's render is driven by the emit, not by the state.
    if (e.key === SIZER_POPUP_FIELDS_STATUS_KEY || !e.key) {
      this.pullPopupFieldsStatus();
    }
    // header.js:220-224 → `processSizerStateData` (:492-510).
    if (e.key === SIZER_POPUP_STATE_KEY || !e.key) {
      void this.applyStoredState(readPopupState());
    }
  };

  /**
   * `header.js:223-231` — RESTORE-ON-LOAD.
   *
   * ```js
   * if (get('sizer_popup'))       self.processSizerData(get('sizer_popup'));       // → update(data)
   * if (get('sizer_popup_state')) self.processSizerStateData(get(…));
   * //   → open_popup(false) | minimize(false) | close_popup(false)   (header.js:470-489)
   * ```
   *
   * Without it a MINIMIZED popup is unrecoverable after a reload: React wrote
   * `sizer_popup_state = "minimized"` on minimize (byte-identical to legacy) but
   * never read it back, so `#floating-icon` was ABSENT on the next load and the
   * user's popup was simply gone; `"opened"` likewise never reopened. Verified
   * on dev3 by injecting one legacy-produced localStorage bag into both pages:
   * legacy restored the icon at `right:256px`, React showed `ABSENT`.
   *
   * `trigger = false` throughout, exactly like `processSizerStateData`, so the
   * restore does not re-write the key it just read.
   */
  private async restoreFromStorage(): Promise<void> {
    if (this.destroyed) return;
    const data = readSizerPopupSelection(); // header.js:223-225
    if (data) this.update(data);
    await this.applyStoredState(readPopupState()); // header.js:227-231
  }

  /**
   * `processSizerStateData` (header.js:492-510) — the state half of both the
   * restore-on-load and the cross-tab listener, so it is one method.
   * `trigger = false` throughout: the state came FROM storage, and re-writing it
   * would broadcast our own restore back to the tab that caused it.
   */
  private async applyStoredState(state: SizerPopupState | null): Promise<void> {
    if (this.destroyed) return;
    switch (state) {
      case POPUP_STATES.OPENED:
        await this.openPopup(false);
        break;
      case POPUP_STATES.MINIMIZED:
        this.minimize(false);
        break;
      case POPUP_STATES.CLOSED:
        this.closePopup(false);
        break;
      default:
        // INITIAL / null / anything else. Legacy's `default:` throws, which is
        // unreachable for the values this app ever writes.
        break;
    }
  }

  destroy(): void {
    this.destroyed = true;
    window.removeEventListener('storage', this.onStorage);
    if (this.forceUpdateTimer) {
      clearTimeout(this.forceUpdateTimer);
      this.forceUpdateTimer = null;
    }
    this.listeners.clear();
  }

  // -------------------------------------------------------------------------
  // fieldsStatus plumbing — :337-389
  // -------------------------------------------------------------------------

  /** `pushPopupFieldsStatus` (:337-341). */
  private pushPopupFieldsStatus(key: string, value: unknown): void {
    this.fieldsStatus[key] = value;
    void this.pushWholeFieldsStatus();
  }

  /** `overrideAndPushFieldsStatus` (:343-350). */
  private overrideAndPushFieldsStatus(obj: SizerPopupFieldsStatus): void {
    this.fieldsStatus = { ...this.fieldsStatus, ...obj };
    void this.pushWholeFieldsStatus();
    this.emit();
  }

  /**
   * `pushWholeFieldsStatus` (:352-379). Persist every NON-NULL entry, then mirror
   * the two project lists back onto the in-memory bag.
   *
   * ⚠ `check` (only `open_popup` passes it) tops the lists up from
   * `saved_*` and, failing that, from the server. `!res.projects` is a
   * TRUTHINESS test, so an EMPTY ARRAY counts as present and no request is made
   * — which, combined with the `sizer_popup`-key guard inside the loaders, is
   * exactly why a fresh page load fires no project request and the first popup
   * OPEN fires two (discovery §7b OBSERVED).
   */
  private async pushWholeFieldsStatus(check = false): Promise<void> {
    const stored = readFieldsStatus();
    const res: SizerPopupFieldsStatus = {};
    Object.keys(this.fieldsStatus).forEach((key) => {
      if (this.fieldsStatus[key] != null) res[key] = this.fieldsStatus[key];
    });
    if (check) {
      if (!res.projects) res.projects = this.savedProjects;
      if (!res.pinned_projects) res.pinned_projects = this.savedPinnedProjects;
      if (!res.projects) res.projects = await loadEditableProjects();
      if (!res.pinned_projects) res.pinned_projects = await loadPinnedProjects();
    }
    this.fieldsStatus.projects = res.projects;
    this.fieldsStatus.pinned_projects = res.pinned_projects;
    if (!deepEqual(stored, res)) writeFieldsStatus(res); // :376-378
  }

  /** `pullPopupFieldsStatus` (:381-389). The `render` argument only decides
   * whether the (DOM) re-render runs; React re-renders off `emit` regardless. */
  private pullPopupFieldsStatus(render = true): void {
    this.fieldsStatus = readFieldsStatus() || {};
    this.checkConfirmationState();
    if (render) this.emit();
  }

  /**
   * `__check_confirmation_state` (:390-421) — APPLY is enabled as soon as ANY
   * section select or ANY tunnel checkbox holds a truthy value.
   */
  private checkConfirmationState(): void {
    const allowing = new Set(SECTION_INDICES.map((i) => `section_${i}_select_value`));
    let state = false;
    for (const key of Object.keys(this.fieldsStatus)) {
      if ((allowing.has(key) || /tunnel_\d+_checkbox_value/.test(key)) && this.fieldsStatus[key]) {
        state = true;
        break;
      }
    }
    this.confirmationState = state;
  }

  // -------------------------------------------------------------------------
  // the cascade — :160-291
  // -------------------------------------------------------------------------

  /**
   * Is the STORED project still one the user may size into?
   *
   * ★ THE ONE BEHAVIOURAL FIX IN THIS FILE. Legacy tests `projects.find(...)`
   * and nothing else (:171), where `projects` is `__loadEditableProjects()` with
   * its DEFAULT arguments — i.e. PAGE ONE of the editable list, 100 rows, and
   * the pinned list is not consulted at all. Every other project therefore
   * failed the test on reload and `updateProjects` blanked the whole selection
   * (project → AHU → tunnels → sections, since each cascade step re-validates
   * against the now-empty list above it). Two ways to land there, both ordinary:
   *
   *  - the project is PINNED. Pinned rows are prepended to the dropdown by
   *    `renderProjects`, so the reporter's project was sitting in the very list
   *    the popup had just rendered while its id was being discarded.
   *  - the project is past the first hundred. Reaching one at all means going
   *    through the dropdown's SEARCH (`searchProjects` → `loadEditableProjects(1,
   *    value)`), so any project a large account has to search for could never
   *    survive a reload.
   *
   * The name re-query is the same endpoint and the same permission filter the
   * unfiltered page-1 call uses, so this cannot resurrect a project the user is
   * not allowed to write; it only stops page 1 being mistaken for the whole set.
   * A miss still falls through to legacy's blanking.
   */
  private async selectedProjectStillAvailable(
    id: string | number,
    projects: ISizerPopupProject[] | undefined,
    pinned: ISizerPopupProject[] | undefined
  ): Promise<boolean> {
    if (projects?.some((el) => el.id == id)) return true; // legacy's own test
    if (pinned?.some((el) => el.id == id)) return true;
    const name = this.fieldsStatus.project_select_name;
    if (!name || name === '--') return false;
    const matches = await loadEditableProjects(1, name);
    return Boolean(matches?.some((el) => el.id == id));
  }

  /** `updateProjects` (:160-186). */
  async updateProjects(overrides: SizerPopupFieldsStatus, save = false): Promise<void> {
    const projects = await loadEditableProjects();
    const pinned = await loadPinnedProjects();
    if (projects) this.savedProjects = projects;
    if (pinned) this.savedPinnedProjects = pinned;
    overrides.projects = projects;
    overrides.pinned_projects = pinned;

    const selected = this.fieldsStatus.project_select_value;
    if (
      // ⚠ THE GUARD IS DELIBERATE: legacy dereferences `projects` unguarded
      // (:171), and it is `undefined` whenever the `sizer_popup` key is absent —
      // a stored project selection plus a cleared `sizer_popup` therefore
      // TypeErrors out of `initialize` and leaves the popup half-built. Falling
      // through to the else branch is what legacy does for every other
      // "selection no longer valid" case.
      selected &&
      (await this.selectedProjectStillAvailable(selected, projects, pinned))
    ) {
      await this.updateAHUs(overrides);
    } else {
      overrides.project_select_value = ''; // :175-176
      overrides.project_select_name = '--';
      await this.updateAHUs(overrides);
    }
    if (save) {
      this.overrideAndPushFieldsStatus(overrides);
      if (this.state == POPUP_STATES.OPENED) this.renderProjects();
    }
  }

  /** `updateAHUs` (:188-216). */
  async updateAHUs(overrides: SizerPopupFieldsStatus, save = false): Promise<void> {
    const merged = { ...this.fieldsStatus, ...overrides };
    const ahus = await loadAhusByProjectId(merged.project_select_value);
    overrides.ahus = ahus;
    if (merged.ahu_select_value && ahus.find((el) => el.id == merged.ahu_select_value)) {
      const tunnels = await this.updateTunnels(overrides);
      await this.updateSections(overrides, tunnels);
    } else {
      overrides.ahu_select_value = '';
      const tunnels = await this.updateTunnels(overrides);
      await this.updateSections(overrides, tunnels);
    }
    if (save) {
      this.overrideAndPushFieldsStatus(overrides);
      // legacy re-renders only the AHU select here (:213); React re-renders all.
    }
  }

  /**
   * `updateTunnels` (:218-252).
   *
   * ⚠ The early bail at :224-229 is the popup's CROSS-PAGE guard: off `/sizer/`
   * it refuses to load tunnels unless the AHU in the URL is the selected one.
   * `window.location.pathname.includes("sizer")` is always true here, so the
   * guard can never fire in the page-scoped port — kept for the record, and it
   * is also the clearest evidence that the popup was written to live on other
   * pages (see the scope decision).
   */
  async updateTunnels(
    overrides: SizerPopupFieldsStatus,
    save = false
  ): Promise<ISizerPopupTunnel[]> {
    const merged = { ...this.fieldsStatus, ...overrides };
    if (
      !window.location.pathname.includes('sizer') &&
      window.location.pathname.replace('/ahu/detail/', '') != merged.ahu_select_value
    ) {
      return [];
    }
    const tunnels = await loadTunnelsByAhuId(merged.ahu_select_value);
    overrides.tunnels = tunnels;

    const ids = new Set(tunnels.map((t) => t.id));
    for (const key of Object.keys(merged)) {
      const match = key.match(/tunnel_(\d+)_checkbox_value/);
      if (match && !ids.has(parseInt(match[1], 10))) {
        overrides[key] = null; // null → dropped from localStorage
      }
    }
    if (save) this.overrideAndPushFieldsStatus(overrides);
    return tunnels;
  }

  /** `updateSections` (:254-291). */
  async updateSections(
    overrides: SizerPopupFieldsStatus,
    tunnels: ISizerPopupTunnel[],
    save = false
  ): Promise<Record<string, ISizerPopupSection[]>> {
    const merged = { ...this.fieldsStatus, ...overrides };
    const sections = await Promise.all(tunnels.map((t) => loadSectionsByTunnelId(t.id)));
    const sectionsMapped: Record<string, ISizerPopupSection[]> = {};
    sections.forEach((rows, i) => {
      sectionsMapped[String(tunnels[i].id)] = rows;
    });
    overrides.sections = sectionsMapped;

    const ids = new Set(sections.flat().map((s) => s.id));
    for (const key of Object.keys(merged)) {
      if (/section_(\d+)_select_value/.test(key)) {
        const id = merged[key];
        if (!ids.has(parseInt(String(id), 10))) overrides[key] = null;
      }
    }
    if (save) {
      this.overrideAndPushFieldsStatus(overrides);
      this.checkConfirmationState(); // :287
      this.emit();
    }
    return sectionsMapped;
  }

  /** `__render_projects` (:590-600) — resets the dropdown list from the bag. */
  private renderProjects(): void {
    this.projectList = [
      ...(this.fieldsStatus.pinned_projects ?? []),
      ...(this.fieldsStatus.projects ?? []),
    ];
    this.emit();
  }

  // -------------------------------------------------------------------------
  // state machine — :425-514, :735-792
  // -------------------------------------------------------------------------

  /** `update` (:425-431) — new cell data; re-render if already open. */
  update(newData: SizerPopupSelection): void {
    this.data = newData;
    if (this.state == POPUP_STATES.OPENED) {
      // `__render_popup` rebuilds the template, whose root class hardcodes
      // `active` (sizer_popup.tmpl:1) — so a re-render UNDOES a backdrop dismiss.
      this.backdropDismissed = false;
      // …and strips `.animate-transition` (:575-577), which only `open_popup`
      // ever puts back (:449-451). So the 0.3 s open animation plays on OPEN and
      // never again, however many cells are clicked afterwards.
      this.animateTransition = false;
      this.emit();
    }
  }

  /** `.animate-transition` on `.animate` — the one-shot open animation. */
  animateTransition = false;

  /**
   * `open_popup` (:434-514).
   *
   * ⚠ THE DOUBLE-CLICK QUIRK: `openSelection` calls `update()` and then
   * `open_popup()` (main.js:635-636). Clicking a SECOND cell while the popup is
   * open therefore re-renders with the new data and then hits the
   * `throw new Error('The popup is already opened.')` at :438-440 — inside an
   * `async` function, so it becomes an unhandled rejection and nothing else
   * happens. Note the two lines BEFORE the throw still ran: `fieldsStatus` was
   * reloaded from storage and `page` was reset to 1. Reproduced exactly (minus
   * the console noise of an unhandled rejection).
   */
  async openPopup(trigger = true): Promise<void> {
    this.fieldsStatus = readFieldsStatus() || {}; // :436
    this.page = 1; // :437
    if (this.state == POPUP_STATES.OPENED) return; // :438-440
    await this.pushWholeFieldsStatus(true); // :441
    if (this.destroyed) return;

    const [tunnelIndexes] = await Promise.all([loadTunnelIndexes(), this.loadingProjects]); // :443
    if (this.destroyed) return;
    for (const index of tunnelIndexes) this.tunnelIndexesMapping[index.id] = index; // :444-446
    this.state = POPUP_STATES.OPENED; // :447
    this.backdropDismissed = false;
    this.animateTransition = true; // :449-451
    this.renderProjects(); // part of `__render_popup` → `__render_projects` (:578)

    /**
     * :478-505 — blank the stored selection of every select that would NOT be
     * renderable for this cell.
     * ⚠ THE LOOP STOPS AT 6. Indices 0-2 have no `INDEX_FIELD_MAPPING` entry at
     * all (see the constant) so they always resolve `isValid = false` and are
     * blanked — for selects that do not exist. 3-5 are the cooling-coil rows and
     * are checked properly. 6-11 (heating + filter) are NEVER blanked: legacy
     * wrote `index < 6` where the select range is 3-11. Reproduced verbatim.
     */
    const overrides: SizerPopupFieldsStatus = {};
    for (let index = 0; index < 6; index += 1) {
      const field = INDEX_FIELD_MAPPING[index];
      const component = field ? FIELD_COMPONENT_MAPPING[field] : undefined;
      let isValid: boolean;
      if (component == 'fan') {
        const fan = (this.data?.fan as Record<string, { wide?: number; high?: number }>)?.[index];
        isValid = Boolean(fan && fan.wide && fan.high);
      } else if (component == 'coil') {
        const coil = this.data?.[field] as { fh?: unknown; fl?: unknown } | undefined;
        isValid = Boolean(coil?.fh && coil?.fl);
      } else if (component == 'filter') {
        const filter = this.data?.[field] as
          | { bank_wide?: unknown; bank_high?: unknown }
          | undefined;
        isValid = Boolean(filter?.bank_wide && filter?.bank_high);
      } else {
        isValid = false; // Missing select
      }
      if (!isValid) overrides[`section_${index}_select_value`] = null;
    }
    this.overrideAndPushFieldsStatus(overrides); // :507
    this.checkConfirmationState(); // :509
    if (trigger) writePopupState(this.state); // :510-512
    this.emit();
  }

  /** `minimize` (:735-751) — the popup collapses to `#floating-icon`. */
  minimize(trigger = true): void {
    if (this.state == POPUP_STATES.CLOSED) return; // :737-741 (legacy throws)
    this.state = POPUP_STATES.MINIMIZED;
    if (trigger) writePopupState(this.state);
    this.emit();
  }

  /**
   * `close_popup` (:755-792) — the FULL teardown. Note it wipes the two keys the
   * sizer PAGE wrote (`sizer_popup`, `d-sizer-airflow`) as well as its own, and
   * blanks the selection half of `fieldsStatus` while KEEPING the cached project
   * lists.
   */
  closePopup(trigger = true): void {
    if (this.state == POPUP_STATES.CLOSED) return; // :757-759 (legacy throws)
    clearDraggablePosition(); // :761 — the literal string "undefined"
    removePopupState(); // :763
    removeLocalStorageItem(SIZER_POPUP_KEY); // :764
    removeLocalStorageItem(SIZER_AIRFLOW_KEY); // :765
    this.filtered = '';
    this.page = 1;
    this.pagination = [];
    this.removeCheckedTunnels(); // :770
    const reset: SizerPopupFieldsStatus = {
      projects: this.fieldsStatus.projects,
      pinned_projects: this.fieldsStatus.pinned_projects,
      project_select_value: '',
      project_select_name: '',
      ahu_select_value: '',
      ahus: [],
      tunnels: [],
      sections: {},
    };
    for (const key of RESET_SECTION_KEYS) reset[key] = '';
    this.overrideAndPushFieldsStatus(reset); // :771-786
    this.state = POPUP_STATES.CLOSED; // :787
    this.renderProjects(); // SearchableSelect.render() at :767-769
    if (trigger) writePopupState(this.state); // :788-790
    this.emit();
  }

  /** `removeChackedTunnels` [sic] (:1191-1196) — drops the checkbox entries from
   * the IN-MEMORY bag only; the caller's `overrideAndPushFieldsStatus` persists. */
  private removeCheckedTunnels(): void {
    Object.keys(this.fieldsStatus).forEach((key) => {
      if (key.includes('_checkbox_value')) delete this.fieldsStatus[key];
    });
  }

  /** The backdrop branch of `main.js:161-167` — see `backdropDismissed`. */
  dismissByBackdrop(): void {
    if (this.state != POPUP_STATES.OPENED || this.backdropDismissed) return;
    this.backdropDismissed = true;
    this.emit();
  }

  // -------------------------------------------------------------------------
  // controls — :296-335, :1014-1096, :150-159, :1198-1214
  // -------------------------------------------------------------------------

  /**
   * `syncChanges` (:296-335) for the two controls whose state it actually owns:
   * the tunnel checkboxes and the section selects. The stored key is
   * mechanically `element.id + '_value'`.
   *
   * Two things the catch-all `change *` binding (:47) does upstream that are
   * deliberately NOT reproduced, because neither is observable:
   *  - It ALSO fires for the AHU select. `specialTargets` (:293) tests
   *    `e.target.name` against `{project_select, ahu_select}`, but that select's
   *    NAME is `ahu_name` (sizer_popup.tmpl:30) — the ids are what those strings
   *    match — so the guard misses and `syncChanges` re-writes
   *    `ahu_select_value` immediately after `changeAHU` (bound first, :43) has
   *    already written the same value. A redundant second write of an identical
   *    value.
   *  - The SearchableSelect's `<input type="search">` has neither `id` nor
   *    `name` (searchable_select.tmpl:13), so a `change` on it (blur/Enter)
   *    reaches `:307-311` and THROWS "Every input has to have unique id". A
   *    latent legacy bug with no user-visible effect (Backbone lets the
   *    exception escape the handler); not ported.
   */
  syncChange(elementId: string, value: string | boolean): void {
    this.pushPopupFieldsStatus(`${elementId}_value`, value);
    if (/^section_\d+_select$/.test(elementId) || /^tunnel_\d+_checkbox$/.test(elementId)) {
      this.checkConfirmationState(); // :330 / :333
    }
    this.emit();
  }

  /** `changeProject` (:1014-1047) — via the SearchableSelect's `select_item`. */
  async changeProject(value: number | string | null, name = '--'): Promise<void> {
    this.page = 1;
    this.removeCheckedTunnels();
    const reset: SizerPopupFieldsStatus = {
      projects: this.fieldsStatus.projects,
      pinned_projects: this.fieldsStatus.pinned_projects,
      project_select_value: (value ?? '') as string | number,
      project_select_name: name,
      ahu_select_value: '',
      ahus: [],
      tunnels: [],
      sections: {},
    };
    for (const key of RESET_SECTION_KEYS) reset[key] = '';
    this.overrideAndPushFieldsStatus(reset);
    this.ahusVersion += 1;
    this.tunnelsVersion += 1;
    this.sectionsVersion += 1;
    const currentAhusVersion = this.ahusVersion;
    void this.searchProjects(''); // :1038 — resets the dropdown's filter
    this.checkConfirmationState();
    this.emit();

    const ahus = await loadAhusByProjectId(value);
    if (currentAhusVersion != this.ahusVersion) return; // :1041
    this.overrideAndPushFieldsStatus({ ahus });
    this.emit();
  }

  /**
   * `changeAHU` (:1050-1096).
   *
   * ★ THE UX DETAIL DISCOVERY OBSERVED: every tunnel of the chosen AHU is
   * auto-CHECKED (:1076-1079) and APPLY is force-enabled (:1080-1081) BEFORE the
   * sections finish loading — which is why picking an AHU is, on its own, enough
   * to make APPLY live.
   */
  async changeAHU(value: string): Promise<void> {
    this.removeCheckedTunnels();
    const reset: SizerPopupFieldsStatus = {
      projects: this.fieldsStatus.projects,
      ahu_select_value: value,
      tunnels: [],
      sections: {},
    };
    for (const key of RESET_SECTION_KEYS) reset[key] = '';
    this.overrideAndPushFieldsStatus(reset);
    this.tunnelsVersion += 1;
    this.sectionsVersion += 1;
    const currentTunnelsVersion = this.tunnelsVersion;
    const currentSectionsVersion = this.sectionsVersion;
    this.checkConfirmationState();
    this.emit();

    const tunnels = await loadTunnelsByAhuId(value);
    if (currentTunnelsVersion != this.tunnelsVersion) return; // :1071

    // Hybrid AHU (KA-x): a back-to-back peer (position > 1) has no checkbox
    // row of its own (displayTunnels collapses it under its position=1
    // sibling) — auto-checking it here would otherwise make apply() below
    // apply it BOTH as its own job and again via its sibling's peer-group
    // expansion.
    const isHybrid = this.isHybridAhu;
    const promises: Promise<ISizerPopupSection[]>[] = [];
    for (const tunnel of tunnels) {
      promises.push(loadSectionsByTunnelId(tunnel.id));
      if (!isHybrid || (tunnel.position ?? 1) === 1) {
        this.pushPopupFieldsStatus(`tunnel_${tunnel.id}_checkbox_value`, true); // :1076-1079
        this.confirmationState = true; // :1080-1081
      }
    }
    this.emit();

    const sections = await Promise.all(promises);
    if (currentSectionsVersion != this.sectionsVersion) return; // :1084

    const sectionsMapped: Record<string, ISizerPopupSection[]> = {};
    sections.forEach((rows, i) => {
      sectionsMapped[String(tunnels[i].id)] = rows;
    });
    this.overrideAndPushFieldsStatus({ tunnels, sections: sectionsMapped });
    this.emit();
  }

  /** `searchProjects` (:150-159) — the SearchableSelect's debounced `search`. */
  async searchProjects(value: string): Promise<void> {
    if (value && this.filtered === value) return;
    const pinned = value
      ? (this.fieldsStatus.pinned_projects ?? []).filter((el) =>
          el.name.toLowerCase().includes(value)
        )
      : (this.fieldsStatus.pinned_projects ?? []);
    this.page = 1;
    this.pagination = [];
    const projects = value
      ? (await loadEditableProjects(1, value)) ?? []
      : this.fieldsStatus.projects ?? [];
    this.projectList = [...pinned, ...projects];
    this.filtered = value;
    this.emit();
  }

  /**
   * `scrollProjects` (:1198-1214) — infinite scroll.
   * ⚠ `self.pagination.length / (page - 1) < 100` is the "last page was short,
   * stop" test, and it runs BEFORE the first append, so `pagination` is seeded
   * from `fieldsStatus.projects` on the very first call (:1202).
   */
  async scrollProjects(): Promise<void> {
    if (this.loadingMore) return;
    const page = this.page ? this.page + 1 : 2;
    if (page === 2) this.pagination = this.fieldsStatus.projects ?? [];
    if (this.pagination.length / (page - 1) < 100) return;
    this.loadingMore = true;
    const list = (await loadEditableProjects(page, this.filtered || undefined)) ?? [];
    this.pagination.push(...list);
    this.page = page;
    const pinned = this.filtered
      ? (this.fieldsStatus.pinned_projects ?? []).filter((el) =>
          el.name.toLowerCase().includes(this.filtered)
        )
      : this.fieldsStatus.pinned_projects ?? [];
    this.projectList = [...pinned, ...this.pagination];
    this.loadingMore = false;
    this.emit();
  }

  // -------------------------------------------------------------------------
  // render helpers (the `__render_*` DOM builders, as pure selectors)
  // -------------------------------------------------------------------------

  /** `__render_ahus` (:602-612) — `--` plus one option per AHU tag. */
  get ahuOptions(): { value: string; label: string }[] {
    return (this.fieldsStatus.ahus ?? []).map((a) => ({ value: String(a.id), label: a.tag }));
  }

  get ahuValue(): string {
    return String(this.fieldsStatus.ahu_select_value ?? '');
  }

  /** `__render_tunnels` (:614-664). */
  get tunnels(): ISizerPopupTunnel[] {
    return this.fieldsStatus.tunnels ?? [];
  }

  /** Hybrid AHU (KA-x): whether the currently SELECTED AHU (not necessarily
   * every AHU in the project list) is hybrid. */
  get isHybridAhu(): boolean {
    const ahu = (this.fieldsStatus.ahus ?? []).find(
      (a) => String(a.id) == this.fieldsStatus.ahu_select_value
    );
    return Boolean(ahu?.is_hybrid);
  }

  /**
   * Hybrid AHU (KA-x) — `__render_tunnels` for hybrid: back-to-back peers
   * (`position > 1`) collapse into their `position === 1` sibling's row, so
   * the popup lists one row per (row, level) group instead of one per
   * physical tunnel. Non-hybrid: unchanged, one row per tunnel.
   */
  get displayTunnels(): ISizerPopupTunnel[] {
    if (!this.isHybridAhu) return this.tunnels;
    return this.tunnels.filter((t) => (t.position ?? 1) === 1);
  }

  /** Every tunnel sharing `tunnel`'s (row, level) — the back-to-back group
   * a bulk sizer apply on `tunnel` must also cover. */
  private rowLevelGroup(tunnel: ISizerPopupTunnel): ISizerPopupTunnel[] {
    return this.tunnels.filter((t) => t.row === tunnel.row && t.level === tunnel.level);
  }

  /** `${row}:${level}` for a hybrid AHU (position is implicit in the group),
   * the legacy `tunnel_indexes_mapping[index_id].name` otherwise. */
  tunnelLabel(tunnel: ISizerPopupTunnel): string {
    if (this.isHybridAhu) return `${tunnel.row ?? 1}:${tunnel.level ?? 1}`;
    return this.tunnelIndexesMapping[tunnel.index_id]?.name ?? '';
  }

  isTunnelChecked(tunnelId: number): boolean {
    return Boolean(this.fieldsStatus[`tunnel_${tunnelId}_checkbox_value`]);
  }

  /** `__render_sections`'s `.non-clickable` gate on `.popup-content__body`
   * (:675-679, :724-732). */
  get selectionActive(): boolean {
    return Boolean(this.fieldsStatus.tunnels && this.fieldsStatus.tunnels.length);
  }

  sectionValue(index: number): string {
    return String(this.fieldsStatus[`section_${index}_select_value`] ?? '');
  }

  /**
   * `__render_sections` (:666-722), as a pure option builder.
   *
   * A section shows up in row `index` iff (a) it is not already taken by ANOTHER
   * row, (b) its `type` matches that row's component
   * (`FIELD_COMPONENT_MAPPING[INDEX_FIELD_MAPPING[index]]`), and (c) its tunnel
   * resolves to a tunnel index name. The label is
   * `"{tunnel index name} : {section name}[ - {tag}]"`.
   *
   * (The third `||` clause at :701-702 — the fan guard — can never be evaluated:
   * it is only reached when `section.type == component`, and `component` is
   * never `'fan'` for indices 3-11. Not ported; it would dereference
   * `this.data.fan[9]`, which does not exist.)
   */
  sectionOptions(index: number): SectionOption[] {
    const used = new Set(
      SECTION_INDICES.map((i) => parseInt(String(this.fieldsStatus[`section_${i}_select_value`]), 10))
    );
    const tunnelsMapping: Record<number, ISizerPopupTunnel> = {};
    for (const tunnel of this.fieldsStatus.tunnels ?? []) tunnelsMapping[tunnel.id] = tunnel;

    const wanted = FIELD_COMPONENT_MAPPING[INDEX_FIELD_MAPPING[index]];
    const mine = this.fieldsStatus[`section_${index}_select_value`];
    const options: SectionOption[] = [{ value: '', label: '--' }];
    const sections = this.fieldsStatus.sections ?? {};
    for (const tunnelId of Object.keys(sections)) {
      for (const section of sections[tunnelId] ?? []) {
        if ((used.has(section.id) && section.id != mine) || section.type != wanted) continue;
        const sectionTunnel = tunnelsMapping[section.tunnel_id];
        if (sectionTunnel == null) continue;
        // Hybrid AHU (KA-x): a section's OWN tunnel needs the full 3-field
        // address (row:level:position) — unlike the grouped tunnel list
        // above, two back-to-back peers' sections must stay distinguishable
        // here.
        let tunnelName: string | undefined;
        if (this.isHybridAhu) {
          tunnelName = `${sectionTunnel.row ?? 1}:${sectionTunnel.level ?? 1}:${sectionTunnel.position ?? 1}`;
        } else {
          if (sectionTunnel.index_id == null) continue;
          tunnelName = this.tunnelIndexesMapping[sectionTunnel.index_id]?.name;
        }
        options.push({
          value: String(section.id),
          label: `${tunnelName} : ${section.name}${section.tag ? ` - ${section.tag}` : ''}`,
        });
      }
    }
    return options;
  }

  /** `#draggable-popup`'s restored position (:452-468). */
  get draggablePosition(): { top?: number | string; left?: number | string } | null {
    const stored = readDraggablePosition();
    if (!stored) return null;
    return { top: stored.top || 0, left: stored.left || 0 };
  }

  /** The `drag` callback's persistence (:470-476) — page offsets, every tick. */
  // eslint-disable-next-line class-methods-use-this
  persistDraggablePosition(position: DraggablePopupPosition): void {
    writeDraggablePosition(position);
  }

  // -------------------------------------------------------------------------
  // ★★ APPLY — :794-895. THE ONLY WRITE PATH IN THE SIZER FEATURE.
  // -------------------------------------------------------------------------

  /** `__get_apply_data_for_cooling_coil` / `_heating_coil` (:920-974). */
  private buildCoilApplyData(field: 'cooling_coil' | 'heating_coil'): Record<string, unknown> {
    const settings = readSizerSettings();
    const prefix = field === 'cooling_coil' ? 'cc' : 'hc';
    const dutyId = field === 'cooling_coil' ? 1 : 2;
    const coil = (this.data?.[field] ?? {}) as Record<string, unknown>;
    const flangeId = settings[`${prefix}_top_and_btm_casing_flange_id`] || 5;
    const flange = settings[`${prefix}_top_and_btm_casing_flange`] || '1.50';
    return {
      // `self.data.airflow` is ALWAYS undefined (main.js:623 reads `.value` off a
      // jQuery object), so this is always the `d-sizer-airflow` fallback.
      airflow_actual: this.data?.airflow || Number(readSizerAirflow()) || undefined,
      duty: field === 'cooling_coil' ? 'cooling' : 'heating',
      duty_id: dutyId,
      qty_stacked: coil.qty,
      casing_flange_top_id: flangeId,
      casing_flange_btm_id: flangeId,
      casing_flange_top: flange,
      casing_flange_btm: flange,
      coil_params: {
        fh: coil.fh,
        fl: coil.fl,
        casing_flange_top_id: flangeId,
        casing_flange_btm_id: flangeId,
        casing_flange_top: flange,
        casing_flange_btm: flange,
        duty_id: dutyId,
      },
    };
  }

  /**
   * `__get_apply_data_for_filter` (:976-995).
   * ⚠ Its defaults DISAGREE with the settings panel's: `max_different_sizes`
   * falls back to 4 here but the panel ships 3 (settings.tmpl), and the
   * velocities are re-clamped against the CELL's own face velocity
   * (`max(200, min(setting, cell))` / `min(750, max(setting, cell))`).
   */
  private buildFilterApplyData(): Record<string, unknown> {
    const settings = readSizerSettings();
    let allowable = '';
    settings.allowable_filter_sizes?.forEach((el, idx) => {
      allowable += `${el.value}${settings.allowable_filter_sizes!.length - 1 != idx ? ',' : ''}`;
    });
    const maxDifferentSizes = settings.max_different_sizes || 4;
    const velMin = settings.filter_face_vel_min || 200;
    const velMax = settings.filter_face_vel_max || 600;
    const filter = (this.data?.filter ?? {}) as Record<string, unknown>;
    return {
      airflow_actual: this.data?.airflow || Number(readSizerAirflow()) || undefined,
      max_different_sizes: maxDifferentSizes,
      allowable_filter_sizes: allowable || 'w12_h24,w24_h12,w20_h20,w20_h24,w24_h20,w24_h24',
      filter_face_vel_min: Math.max(200, Math.min(Number(velMin), Number(filter.face_velocity))),
      filter_face_vel_max: Math.min(750, Math.max(Number(velMax), Number(filter.face_velocity))),
      size_qty_id: filter.size_qty_id,
    };
  }

  /**
   * `apply` (:794-895). The request ORDER is load-bearing and is reproduced
   * exactly:
   *
   *   1. show `#successful_apply` OPTIMISTICALLY, before any request (:804 —
   *      `__successfulApply` also arms a 3 s auto-hide);
   *   2. walk `fieldsStatus` and collect the tunnel + section jobs (:808-859);
   *   3. `sizer_doesnt_need_update = "true"` IF there are section jobs (:861);
   *   4. `await` the ONE unit POST (:863-866);
   *   5. `await Promise.all(...)` the tunnel POSTs — parallel (:867-875);
   *   6. clear `sizer_doesnt_need_update` (:876);
   *   7. `await Promise.all(...)` the section POSTs — parallel (:877-884);
   *   8. `Dispatcher.trigger('coil:model:change','change')` (:885) — see below;
   *   9. hide `#successful_apply` (:887).
   *
   * ⚠ THE POPUP DOES NOT CLOSE ON APPLY. It stays open with every selection
   * intact, so the same cell can be applied to another AHU immediately.
   *
   * ⚠ THE ERROR PATH IS DEAD (:888-892). The `catch` runs
   * `fn.uni_tools.localStorage.remove(...)` first — and `fn.uni_tools` has NO
   * `localStorage` member (grepped) — so the catch itself TypeErrors and
   * `__failedApply()` is unreachable: `#failed_apply` can never be shown. And
   * because `fn.uni_tools.post` only throws when the body fails to parse as JSON
   * (see `sizerPopupPost`), a DRF 400 does not even reach the catch. Reproduced:
   * the flag is cleared, no failure banner is shown.
   *
   * ⚠ `Dispatcher.trigger('coil:model:change')` is a NO-OP for the sizer — its
   * dispatcher registers no handlers at all (`controller/dispatcher.js:17-21`,
   * all three entries commented out; discovery §10). It exists for the header's
   * OTHER hosts (the AHU board), which the page-scoped port does not have. Not
   * ported; recorded here.
   */
  async apply(): Promise<void> {
    if (this.state != POPUP_STATES.OPENED) return; // :796-798 (legacy throws)
    if (!this.confirmationState) return; // :799-803 (legacy throws)
    this.showSuccess?.(); // :804

    const tunnelJobs: {
      id: string;
      width: unknown;
      height: unknown;
      casing_insulation_id: number | null;
    }[] = [];
    const sectionJobs: { sectionId: string; component: string; data: Record<string, unknown> }[] =
      [];

    for (const key of Object.keys(this.fieldsStatus)) {
      const sectionMatch = key.match(/section_(\d+)_select_value/);
      if (sectionMatch) {
        if (!this.fieldsStatus[key]) continue;
        const index = Number(sectionMatch[1]);
        const field = INDEX_FIELD_MAPPING[index];
        const component = FIELD_COMPONENT_MAPPING[field];
        // :815 + :826 — the section id comes off the DOM `<select>`, not the bag.
        // Identical in practice; kept so a value the select rejected (because it
        // is no longer an option) produces legacy's empty-id URL, not a stale id.
        const el = document.getElementById(`section_${index}_select`) as HTMLSelectElement | null;
        // `field === 'filter'` ⟺ `component === 'filter'` (FIELD_COMPONENT_MAPPING),
        // so this is legacy's `component == 'fan' ? … : __get_apply_data_for_{field}`
        // dispatch (:817-822) with the fan arm dropped — it is unreachable.
        const data =
          field === 'filter' ? this.buildFilterApplyData() : this.buildCoilApplyData(field);
        const sectionId = el ? el.value : '';
        const coilParams = data.coil_params as { fh?: unknown; fl?: unknown } | undefined;
        if (coilParams) {
          data.fh = coilParams.fh; // :827-830
          data.fl = coilParams.fl;
        }
        data.component_type = component; // :831
        sectionJobs.push({ sectionId, component, data });
      } else {
        const tunnelMatch = key.match(/tunnel_(\d+)_checkbox_value/);
        if (tunnelMatch && this.fieldsStatus[key]) {
          tunnelJobs.push({
            id: tunnelMatch[1],
            width: this.data?.current_width, // :853 — the STRING "84.00"
            height: this.data?.current_height,
            casing_insulation_id: (this.data?.casing_insulation_id as number) || null,
          });
        }
      }
    }

    try {
      if (sectionJobs.length > 0) setDoesntNeedUpdate(); // :861
      await applyUnitSizer(this.fieldsStatus.ahu_select_value, {
        construction_class_id: Number(this.data?.construction_class_id) || 1, // :864
        airflow_actual: this.data?.airflow || Number(readSizerAirflow()) || '', // :865
      });
      if (this.isHybridAhu) {
        // Hybrid AHU (KA-x): each checked job is a position=1 tunnel (see
        // changeAHU) — apply it to every back-to-back peer sharing its
        // (row, level) in ONE bulk request, or the peers keep their old size.
        await Promise.all(
          tunnelJobs.map((job) => {
            const tunnel = this.tunnels.find((t) => String(t.id) === job.id);
            // rowLevelGroup always includes `tunnel` itself (row/level match
            // trivially), so this is only empty when the tunnel id is gone.
            const peers = tunnel ? this.rowLevelGroup(tunnel) : [];
            if (!peers.length) return Promise.resolve(undefined);
            return applyTunnelSizerBulk(
              peers.map((peer) => ({
                tunnel_id: peer.id,
                width: job.width,
                height: job.height,
                casing_insulation_id: job.casing_insulation_id || null,
              }))
            );
          })
        );
      } else {
        await Promise.all(
          tunnelJobs.map((job) =>
            applyTunnelSizer(job.id, {
              width: job.width,
              height: job.height,
              casing_insulation_id: job.casing_insulation_id || null,
            })
          )
        );
      }
      removeDoesntNeedUpdate(); // :876
      await Promise.all(
        sectionJobs.map((job) =>
          applySectionSizer(job.sectionId, { component: job.component, ...job.data })
        )
      );
      this.hideSuccess?.(); // :887
      this.scheduleForceUpdateAfterApply();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err); // :890
      removeDoesntNeedUpdate();
      // :892 `__failedApply()` — unreachable upstream, see the docblock.
    }
  }

  /** `header.js:431-453` `forceUpdateAHUs` / `forceUpdateTunnels` debounce. */
  private forceUpdateTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * The POST-APPLY REFRESH (`header.js:431-453`, reached via the syncdata socket).
   *
   * Legacy does not refresh from `apply()` itself. The three `sizer_apply` POSTs
   * make the server broadcast `syncdata` messages, `fn.uni_tools.onSocketData`
   * (header.js:247-355) maps `model: "ahuunit"` → `forceUpdateAHUs()` and
   * `model: "ahutunnels"` → `forceUpdateTunnels()`, and each re-fetches and
   * re-renders the popup after a 2000 ms debounce — unless
   * `localStorage.sizer_doesnt_need_update` is set (header.js:432, :448).
   *
   * OBSERVED on dev3 (AHU 3833 / tunnel 5436, stack-traced): after the two
   * write POSTs legacy fires
   *   GET /ahu/tunnels/?ahu_id=3833      ← updateTunnels     (header.js:451)
   *   GET /ahu/units_sizer/?project_id=  ┐
   *   GET /ahu/tunnels/?ahu_id=3833      ├ updateAHUs        (header.js:436)
   *   GET /ahu/sections/?tunnel_id=5436  ┘
   * and CURRENT OD W x H flips 100.00 x 100.00 → 84.00 x 92.00. Without it the
   * React popup kept showing the PRE-apply 100.00 x 100.00 — the user's own
   * write silently not reflected.
   *
   * `updateAHUs` re-enters `updateTunnels` + `updateSections` (:202-203), so one
   * call reproduces the whole observed GET set and the same end state.
   *
   * ⚠ SCOPE: this reproduces the refresh for the write THIS page performs. The
   * general socket-driven refresh (another user/tab changing the same AHU) is
   * still not wired in React — a documented remaining deviation, not a
   * regression introduced here.
   */
  private scheduleForceUpdateAfterApply(): void {
    if (this.forceUpdateTimer) clearTimeout(this.forceUpdateTimer); // header.js:434
    this.forceUpdateTimer = setTimeout(() => {
      this.forceUpdateTimer = null;
      // header.js:432 / :448 — the syncdata refreshers bail while the flag is up.
      if (readDoesntNeedUpdate()) return;
      if (this.destroyed) return;
      if (this.state != POPUP_STATES.OPENED) return;
      void this.updateAHUs({}, true);
    }, 2000); // header.js:435, :450 — the 2 s debounce
  }

  /** Wired by the component to the `#successful_apply` banner (`__successfulApply`
   * / :997-1003 and the explicit hide at :887). */
  showSuccess?: () => void;
  hideSuccess?: () => void;
}
